import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { locationElocService } from '../../services';
import _ from 'lodash';
import './OperationDetails.css';
import { useDispatch } from 'react-redux';
import { actions as appConfigActions } from 'redux/appConfig';
import storage from 'lib/storage';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useOnboarding } from './OnboardingContext';
import PillSelector from 'components/PillSelector';
import { getTypeOfIndividualEnumName } from './enumData';
import { useIsMobile } from 'lib/utils';
import config from 'config';

const businessTypes = [
  { value: '', label: 'Select business type' },
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'private-limited', label: 'Private Limited' },
  { value: 'public-limited', label: 'Public Limited' },
  { value: 'llp', label: 'Limited Liability Partnership' },
];

const validationSchema = Yup.object().shape({
  isVerifiedPerson: Yup.string().required('Please select an option'),
  consentGiven: Yup.string().when('isVerifiedPerson', {
    is: 'no',
    then: Yup.string().required('Please provide consent to proceed'),
    otherwise: Yup.string(),
  }),
  sameAsFactory: Yup.boolean().required('Please select an option'),
  // For now, keep location optional regardless of toggle state
  location: Yup.object().shape({
    addressLine1: Yup.string(),
    addressLine2: Yup.string(),
    addressLine3: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    pincode: Yup.string().matches(/^\d{6}$/, 'Pincode must be 6 digits').nullable().notRequired(),
  }),
  // Custom validation for pincode mismatch
  pincodeMismatch: Yup.boolean().test(
    'pincode-match',
    'Pincode mismatch detected. Please ensure your business address matches your official records.',
    function() {
      // This will be set by the component state, not form values
      return true; // Always pass this validation, we'll handle it in the component
    }
  ),
  manualAddress: Yup.object().when('isAddressFromDocMissing', {
    is: true,
    then: Yup.object().shape({
      line_1: Yup.string().required('Address Line 1 is required').min(3, 'Character length should be between 3 and 100').max(100, 'Character length should be between 3 and 100'),
      line_2: Yup.string()
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .min(3, 'Character length should be between 3 and 100')
        .max(100, 'Character length should be between 3 and 100'),
      street_name: Yup.string()
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .min(3, 'Character length should be between 3 and 100')
        .max(100, 'Character length should be between 3 and 100'),
      city: Yup.string().required('City is required'),
      state: Yup.string().required('State is required'),
      zip: Yup.string().required('Pin code is required').matches(/^[0-9]{6}$/, 'Must be 6 digits'),
      district_code: Yup.string().required('District is required'),
    }),
    otherwise: Yup.object().notRequired()
  }),
});


// location: Yup.object().when('sameAsFactory', {
//   is: false,
//   then: Yup.object().shape({
//     addressLine1: Yup.string().required('Address Line 1 is required'),
//     addressLine2: Yup.string(),
//     addressLine3: Yup.string(),
//     city: Yup.string().required('City is required'),
//     state: Yup.string().required('State is required'),
//     pincode: Yup.string()
//       .required('Pincode is required')
//       .matches(/^\d{6}$/, 'Pincode must be 6 digits'),
//   }),
//   otherwise: Yup.object().shape({
//     addressLine1: Yup.string(),
//     addressLine2: Yup.string(),
//     addressLine3: Yup.string(),
//     city: Yup.string(),
//     state: Yup.string(),
//     pincode: Yup.string(),
//   }),

const OperationDetails = forwardRef(({ title = 'Operation Details', onPrevious, onComplete, validatedPincode, fetchedRegisteredAddress, fetchedAddressSource, authorizedSignatories = [], companyName, isNextButtonDisable, setIsNextButtonDisable }, ref) => {
  console.log('fetchedRegisteredAddress', fetchedRegisteredAddress);
  console.log('fetchedAddressSource', fetchedAddressSource);
  console.log('validatedPincode', validatedPincode);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [mapToken, setMapToken] = useState('');
  const [jwtToken, setJwtToken] = useState('');
  const [locationPincodeMismatch, setLocationPincodeMismatch] = useState(false);
  const [isDistrictServiceable, setIsDistrictServiceable] = useState(true);
  const [districtServiceMessage, setDistrictServiceMessage] = useState('');
  const [hasPickedLocation, setHasPickedLocation] = useState(false);
  const [selectedSignatory, setSelectedSignatory] = useState('');
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const {
    updateSection,
    markStepComplete,
    markStepIncomplete,
    getStepData
  } = useOnboarding();
console.log("Authorized Signatories in OperationDetails", authorizedSignatories);
  // Get existing data for this step
  const existingData = getStepData(2); // Step 2 is address
  const businessData = getStepData(1) || {}; // Step 1 is businessDetails
  const effectiveValidatedPincode = validatedPincode || businessData?.validatedPincode || existingData?.validatedPincode || '';
  const effectiveFetchedRegisteredAddress = fetchedRegisteredAddress || businessData?.fetchedRegisteredAddress || existingData?.fetchedRegisteredAddress || '';
  const effectiveFetchedAddressSource = fetchedAddressSource || businessData?.fetchedAddressSource || existingData?.fetchedAddressSource || '';
  // Determine if we should hide the factory address toggle
  const typeEnumName = getTypeOfIndividualEnumName(Number(businessData?.typeOfIndividual || 0));
  const verifiedUsingDL = Boolean(businessData?.hasIndividualDL || businessData?.validatedInfo?.individual);
  const hideFactoryToggle = verifiedUsingDL && (typeEnumName === 'TransportCompany' || typeEnumName === 'DriverAndVehicleOwner');

  // Manual Address Logic
  const isAddressFromDocMissing = businessData?.isAddressFromDocMissing || false;
  const missingAddressFields = businessData?.missingAddressFields || [];
  const prefetchedManualAddress = businessData?.prefetchedManualAddress || {};

  const mapplsAccessToken = config.mapplsAccessToken || '';

  // Initialize state from existing data - RUN ONLY ONCE ON MOUNT
  useEffect(() => {
    if (existingData.selectedSignatory) {
      setSelectedSignatory(existingData.selectedSignatory);
    }
    if (existingData.locationPincodeMismatch !== undefined) {
      setLocationPincodeMismatch(existingData.locationPincodeMismatch);
    }
    if (existingData.isDistrictServiceable !== undefined) {
      setIsDistrictServiceable(existingData.isDistrictServiceable);
    }
    if (existingData.districtServiceMessage) {
      setDistrictServiceMessage(existingData.districtServiceMessage);
    }
    if (existingData.hasPickedLocation !== undefined) {
      setHasPickedLocation(existingData.hasPickedLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount to avoid overwriting local state when context updates

  // Fetch JWT token for SystemIndentPersonJWT
  useEffect(() => {
    (async () => {
      try {
        const token = await dispatch(appConfigActions.getAppConfig({ config_parameter: 'SystemIndentPersonJWT' }));
        if (token) {
          setJwtToken(token);
          await storage.setItem('accessToken', token);
        }
      } catch (err) {
        console.error('Error fetching SystemIndentPersonJWT:', err);
      }
    })();
  }, [dispatch]);

  // Get map token on component mount
  useEffect(() => {
    const getMapToken = async () => {
      try {
        const tokenResponse = await locationElocService.getOAuthToken();
        if (tokenResponse && tokenResponse.access_token) {
          setMapToken(tokenResponse.access_token);
        }
      } catch (error) {
        console.error('Error getting map token:', error);
      }
    };
    getMapToken();
  }, []);

  // Auto-select the first signatory by default when list is available
  useEffect(() => {
    if (authorizedSignatories.length > 0 && !selectedSignatory) {
      const firstSignatory = authorizedSignatories[0]?.name || authorizedSignatories[0];
      setSelectedSignatory(firstSignatory);
    }
    if (authorizedSignatories.length === 0 && selectedSignatory) {
      setSelectedSignatory('');
    }
  }, [authorizedSignatories, selectedSignatory]);

  const toggleLocationModal = () => {
    setIsLocationModalOpen(!isLocationModalOpen);
  };

  // Helper to clear location fields
  const clearLocation = (setFieldValue) => {
    setFieldValue('location', {
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      city: '',
      state: '',
      pincode: '',
      fullAddress: '',
      lat: '',
      lon: ''
    });
    // Clear pincode mismatch warning when location is reset
    setLocationPincodeMismatch(false);
    setIsDistrictServiceable(true);
    setDistrictServiceMessage('');
    setHasPickedLocation(false);
    updateSection('address', { hasPickedLocation: false });
  };

  const handleLocationSelect = async (locationData, setFieldValue) => {
    try {
      const params = {
        Latitude: locationData.lat,
        Longitude: locationData.lng,
      };
      const resData = await locationElocService.getReverseLocationDetailswithJWT(params, jwtToken);
      const locationDetails = resData?.results?.[0] || {};

      // Compose address fields
      const addressLine1 = [locationDetails.houseNumber, locationDetails.houseName].filter(Boolean).join(', ');
      const addressLine2 = [locationDetails.street, locationDetails.subSubLocality].filter(Boolean).join(', ');
      const addressLine3 = [locationDetails.subLocality, locationDetails.locality].filter(Boolean).join(', ');
      const city = locationDetails.city || locationDetails.village || '';
      const state = locationDetails.state || '';
      const pincode = locationDetails.pincode || '';
      const fullAddress = locationDetails.formatted_address || '';
      const lat = locationDetails.lat || '';
      const lon = locationDetails.lng || '';
      const Country_Code = locationDetails.area || '';
      const District_Code = locationDetails.district || '';
      const Locality = locationDetails.locality || '';
      const Sub_District_Code = locationDetails.subDistrict || '';
      const sub_Locality_Code = locationDetails.subLocality || '';
      const Village_Code = locationDetails.village || '';

      setFieldValue('location', {
        addressLine1,
        addressLine2,
        addressLine3,
        city,
        state,
        pincode,
        fullAddress,
        lat,
        lon,
        Country_Code,
        District_Code,
        Locality,
        Sub_District_Code,
        sub_Locality_Code,
        Village_Code
      });

      // Check district serviceability
      if (District_Code) {
        try {
          const params = {
            District_Code: District_Code,
          };
          const serviceRes = await locationElocService.checkDistrictServiceability(params);
          console.log('serviceRes---->', serviceRes);
          const isServiceable = serviceRes?.is_circle_exists;
          setIsDistrictServiceable(isServiceable);
          setDistrictServiceMessage(serviceRes?.message || (isServiceable ? '' : 'We currently don’t serve your area.'));
        } catch (serviceErr) {
          setIsDistrictServiceable(true);
          setDistrictServiceMessage('');
        }
      } else {
        setIsDistrictServiceable(true);
        setDistrictServiceMessage('');
      }

      // Compare picked pincode with validated pincode immediately after location selection
      if (effectiveValidatedPincode && pincode && effectiveValidatedPincode !== pincode) {
        setLocationPincodeMismatch(true);
        console.log('Pincode mismatch detected:', { validatedPincode: effectiveValidatedPincode, pickedPincode: pincode });
      } else {
        setLocationPincodeMismatch(false);
        console.log('Pincode match or no validation needed:', { validatedPincode: effectiveValidatedPincode, pickedPincode: pincode });
      }

      toggleLocationModal();
      setHasPickedLocation(true);
      updateSection('address', { hasPickedLocation: true });
    } catch (error) {
      console.error('Error getting location details:', error);
    }
  };

  const initialValues = {
    isVerifiedPerson: existingData.isVerifiedPerson || 'yes',
    consentGiven: existingData.consentGiven || 'yes',
    sameAsFactory: existingData.sameAsFactory !== undefined ? existingData.sameAsFactory : true,
    fetchedRegisteredAddress: existingData.fetchedRegisteredAddress || businessData.fetchedRegisteredAddress || '',
    fetchedAddressSource: existingData.fetchedAddressSource || businessData.fetchedAddressSource || '',
    isAddressFromDocMissing: businessData?.isAddressFromDocMissing || false,
    manualAddress: {
      line_1: businessData.prefetchedManualAddress?.line_1 || existingData.manualAddress?.line_1 || '',
      line_2: businessData.prefetchedManualAddress?.line_2 || existingData.manualAddress?.line_2 || '',
      street_name: businessData.prefetchedManualAddress?.street_name || existingData.manualAddress?.street_name || '',
      zip: businessData.prefetchedManualAddress?.zip || existingData.manualAddress?.zip || '',
      city: businessData.prefetchedManualAddress?.city || existingData.manualAddress?.city || '',
      state: businessData.prefetchedManualAddress?.state || existingData.manualAddress?.state || '',
      district_code: businessData.prefetchedManualAddress?.district_code || (existingData.manualAddress?.city == businessData.prefetchedManualAddress?.city ? existingData.manualAddress?.district_code : '') || '',
    },
    location: {
      addressLine1: existingData.addressLine1 || '',
      addressLine2: existingData.addressLine2 || '',
      addressLine3: existingData.addressLine3 || '',
      city: existingData.city || '',
      state: existingData.state || '',
      pincode: existingData.pincode || '',
      fullAddress: existingData.fullAddress || '',
      lat: existingData.lat || '',
      lon: existingData.lon || ''
    }
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    console.log('=== OperationDetails handleSubmit START ===');
    console.log('OperationDetails handleSubmit called with values:', values);
    console.log('onComplete function exists:', !!onComplete);

    // Check for pincode mismatch before proceeding
    if (locationPincodeMismatch) {
      setFieldError('pincodeMismatch', 'Pincode mismatch detected. Please ensure your business address matches your official records.');
      setSubmitting(false);
      return;
    }

    // Prevent proceeding if district is not serviceable
    // if (!isDistrictServiceable) {
    //   setFieldError('districtServiceability', 'We currently do not serve your area. You cannot proceed with onboarding for this location.');
    //   setSubmitting(false);
    //   return;
    // }

    // If authorizedSignatories are present, require selection
    if (authorizedSignatories.length > 0 && !selectedSignatory) {
      setFieldError('selectedSignatory', 'Please select an authorized signatory.');
      setSubmitting(false);
      return;
    }

    // 1. Prepare merged PAN/GST data (Transferred from BusinessDetails logic to handle manual edits)
    let finalBusinessDetails = { ...businessData };
    let combinedFullAddress = "";
    let finalAddressSource = "";

    const manualEntries = {};
    if (values.manualAddress) {
      Object.keys(values.manualAddress).forEach(key => {
        if (values.manualAddress[key] && values.manualAddress[key].trim() !== "") {
          manualEntries[key] = values.manualAddress[key].trim();
        }
      });
    }

    if (businessData.hasPAN && businessData.validatedInfo?.pan) {
      finalAddressSource = 'PAN';
      // Deep clone to avoid mutations using lodash
      let finalPanResult = _.cloneDeep(businessData.validatedInfo.pan);
      let finalFullPanResponse = _.cloneDeep(businessData.fullValidatedInfo?.pan);

      const originalAddress = finalPanResult.user_address || {};

      // Merge: Original API data + Manual overrides
      const updatedAddress = {
        ...originalAddress,
        ...manualEntries
      };

      // Build full address string for PAN
      combinedFullAddress = [
        updatedAddress.line_1,
        updatedAddress.line_2,
        updatedAddress.street_name,
        updatedAddress.city,
        updatedAddress.state,
        updatedAddress.zip
      ].filter(val => val && val.trim() !== "").join(', ').trim();

      updatedAddress.full = combinedFullAddress;
      updatedAddress.district_code = values.manualAddress?.district_code || "";
      finalPanResult.user_address = updatedAddress;

      if (finalFullPanResponse?.result) {
        finalFullPanResponse.result.user_address = updatedAddress;
      }

      finalBusinessDetails.validatedInfo = { ...finalBusinessDetails.validatedInfo, pan: finalPanResult };
      finalBusinessDetails.fullValidatedInfo = { ...finalBusinessDetails.fullValidatedInfo, pan: finalFullPanResponse };
      finalBusinessDetails.panValidated = finalPanResult;
    }

    if (businessData.hasGST && businessData.validatedInfo?.gst) {
      finalAddressSource = 'GSTIN';
      let finalGstResult = _.cloneDeep(businessData.validatedInfo.gst);
      let finalFullGstResponse = _.cloneDeep(businessData.fullValidatedInfo?.gst);

      const originalAddressStr = finalGstResult?.primary_business_address?.registered_address || "";
      const addressParts = originalAddressStr.split(', ');

      // Construct a new registered_address string for GST
      // Indices: 0:line1, 1:line2, 3:street_name, len-3:city, len-2:state, len-1:zip
      const updatedParts = [...addressParts];
      if (manualEntries.line_1) updatedParts[0] = manualEntries.line_1;
      if (manualEntries.line_2) updatedParts[1] = manualEntries.line_2;
      if (manualEntries.street_name) updatedParts[3] = manualEntries.street_name;
      if (manualEntries.city && updatedParts.length >= 3) updatedParts[updatedParts.length - 3] = manualEntries.city;
      if (manualEntries.state && updatedParts.length >= 2) updatedParts[updatedParts.length - 2] = manualEntries.state;
      if (manualEntries.zip && updatedParts.length >= 1) updatedParts[updatedParts.length - 1] = manualEntries.zip;

      combinedFullAddress = updatedParts.join(', ');
      finalGstResult.primary_business_address.registered_address = combinedFullAddress;
      finalGstResult.primary_business_address.district_code = values.manualAddress?.district_code || "";

      if (finalFullGstResponse?.result?.primary_business_address) {
        finalFullGstResponse.result.primary_business_address.registered_address = combinedFullAddress;
        finalFullGstResponse.result.primary_business_address.district_code = values.manualAddress?.district_code || "";
      }

      finalBusinessDetails.validatedInfo = { ...finalBusinessDetails.validatedInfo, gst: finalGstResult };
      finalBusinessDetails.fullValidatedInfo = { ...finalBusinessDetails.fullValidatedInfo, gst: finalFullGstResponse };
      finalBusinessDetails.gstValidated = finalGstResult;
    }

    if (businessData.hasIndividualDL && businessData.validatedInfo?.individual) {
      finalAddressSource = 'Driving Licence';
      let finalIndividualResult = _.cloneDeep(businessData.validatedInfo.individual);
      let finalFullIndividualResponse = _.cloneDeep(businessData.fullValidatedInfo?.individual);

      const dlAddresses = finalIndividualResult.user_address || [];
      const permanentIdx = dlAddresses.findIndex(a => a.type === 'Permanent');
      const idx = permanentIdx >= 0 ? permanentIdx : 0;
      const originalAddr = dlAddresses[idx] || {};

      const updatedAddr = { ...originalAddr };
      if (manualEntries.line_1) updatedAddr.addressLine1 = manualEntries.line_1;
      if (manualEntries.line_2) updatedAddr.addressLine2 = manualEntries.line_2;
      if (manualEntries.street_name) updatedAddr.addressLine3 = manualEntries.street_name;
      if (manualEntries.city) updatedAddr.city = manualEntries.city;
      if (manualEntries.state) updatedAddr.state = manualEntries.state;
      if (manualEntries.zip) updatedAddr.pin = manualEntries.zip;
      if (manualEntries.district_code) updatedAddr.district = manualEntries.district_code;

      combinedFullAddress = [
        updatedAddr.addressLine1,
        updatedAddr.addressLine2,
        updatedAddr.addressLine3,
        updatedAddr.city,
        updatedAddr.state,
        updatedAddr.pin,
      ].filter(val => val && val.trim() !== '').join(', ').trim();

      updatedAddr.completeAddress = combinedFullAddress;

      if (finalIndividualResult.user_address[idx] !== undefined) {
        finalIndividualResult.user_address[idx] = updatedAddr;
      }
      if (finalFullIndividualResponse?.result?.user_address?.[idx] !== undefined) {
        finalFullIndividualResponse.result.user_address[idx] = updatedAddr;
      }

      finalBusinessDetails.validatedInfo = { ...finalBusinessDetails.validatedInfo, individual: finalIndividualResult };
      finalBusinessDetails.fullValidatedInfo = { ...finalBusinessDetails.fullValidatedInfo, individual: finalFullIndividualResponse };
    }

    // Update persistence fields in businessDetails section of session data
    if (combinedFullAddress) {
      finalBusinessDetails.fetchedRegisteredAddress = combinedFullAddress;
      finalBusinessDetails.fetchedAddressSource = finalAddressSource;
      finalBusinessDetails.validatedPincode = manualEntries.zip || finalBusinessDetails.validatedPincode;

      // Update businessDetails section in context
      updateSection('businessDetails', finalBusinessDetails);
    }

    // Save complete data to context (address section)
    const completeData = {
      ...values,
      ...values.location,
      selectedSignatory,
      locationPincodeMismatch,
      isDistrictServiceable,
      districtServiceMessage,
      authorizedSignatories,
      companyName
    };

    console.log('completeData123', completeData);

    updateSection('address', completeData);

    // Mark step as complete
    if (authorizedSignatories.length === 0 || selectedSignatory) {
      markStepComplete(2);
    } else {
      markStepIncomplete(2);
    }

    // Call onComplete with the step data to trigger savePreRegistrationData
    if (onComplete) {
      const stepData = {
        operationDetails: {
          isVerifiedPerson: values.isVerifiedPerson,
          consentGiven: values.consentGiven,
          location: values.location,
          manualAddress: values.manualAddress,
          selectedSignatory: selectedSignatory
        },
        // IMPORTANT: Also pass the updated businessDetails so savePreRegistrationData uses the merged address
        businessDetails: finalBusinessDetails
      };
      console.log('Calling onComplete with stepData:', stepData);
      onComplete(stepData);
    } else {
      console.error('onComplete function exists:', !!onComplete);
    }

    console.log('=== OperationDetails handleSubmit END ===');
    setSubmitting(false);
  };

  return (
    <div className="step-form">
      <h2>{title}</h2>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={ref}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, touched, errors, setFieldValue, submitForm }) => {
          useEffect(() => {
            let addressHasError = false;

            if (values.isAddressFromDocMissing) {
              const currentMissingFields = missingAddressFields || [];
              const hasEmptyMandatoryFields = currentMissingFields.some(
                (key) => {
                  const isOptional = ['line_2', 'street_name'].includes(key);
                  if (isOptional) return false;
                  return !values.manualAddress?.[key] || values.manualAddress[key].trim() === "";
                }
              );

              const hasValidationErrors = errors.manualAddress && Object.keys(errors.manualAddress).length > 0;
              if (hasEmptyMandatoryFields || hasValidationErrors) {
                addressHasError = true;
              }
            }

            if ((values?.isVerifiedPerson === 'no' && values?.consentGiven === 'no') || addressHasError) {
              setIsNextButtonDisable(true);
            } else {
              setIsNextButtonDisable(false);
            }
          }, [values?.isVerifiedPerson, values?.consentGiven, values.manualAddress, errors.manualAddress, values.isAddressFromDocMissing]);
          useImperativeHandle(ref, () => ({ submitForm }));

          return (
            <Form>

              {/* Identity Verification Section */}
              <div className="form-section mb-4">
                {/* <h3 className="mb-3">Identity Verification</h3> */}
                <div className="form-field">
                  {authorizedSignatories.length > 0 && (
                    <label>Are you one of these authorized signatories?</label>
                  )}
                  {!authorizedSignatories.length && (
                    <label>Are you the authorized signatory for this business?</label>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1rem 0' }}>
                    <label className="ncswitch">
                      <input
                        type="checkbox"
                        checked={values.isVerifiedPerson === 'yes'}
                        onChange={e => setFieldValue('isVerifiedPerson', e.target.checked ? 'yes' : 'no')}
                      />
                      <span className="ncswitch-slider"></span>
                    </label>
                    <span style={{ fontWeight: 500, color: '#757d84' }}>{values.isVerifiedPerson === 'yes' ? 'Yes' : 'No'}</span>
                  </div>
                    {!hideFactoryToggle && (
                  <div className="form-field mb-4">
                    <label style={{ fontWeight: 500, marginRight: '1rem' }}>Is the registered address also the factory address?</label>
                    <label className="ncswitch">
                      <input
                        type="checkbox"
                        checked={values.sameAsFactory}
                        onChange={e => {
                          const checked = e.target.checked;
                          setFieldValue('sameAsFactory', checked);
                          updateSection('address', { sameAsFactory: checked });
                        }}
                      />
                      <span className="ncswitch-slider"></span>
                    </label>
                    <span style={{ fontWeight: 500, color: '#757d84', marginLeft: '0.5rem' }}>{values.sameAsFactory ? 'Yes' : 'No'}</span>
                  </div>
                  )}
                  <ErrorMessage name="isVerifiedPerson" component="div" className="error-message" />
                </div>

                {values.isVerifiedPerson === 'no' && (
                  <div className="form-field mt-3">
                    <label>Do you have consent to proceed on behalf of the registered person?</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1rem 0' }}>
                      <label className="ncswitch">
                        <input
                          type="checkbox"
                          checked={values.consentGiven === 'yes'}
                          onChange={e => setFieldValue('consentGiven', e.target.checked ? 'yes' : 'no')}
                        />
                        <span className="ncswitch-slider"></span>
                      </label>
                      <span style={{ fontWeight: 500, color: '#757d84' }}>{values.consentGiven === 'yes' ? 'Yes' : 'No'}</span>
                    </div>
                    <ErrorMessage name="consentGiven" component="div" className="error-message" />
                  </div>
                )}

                {/* Authorized Signatory Selection */}
                {authorizedSignatories.length > 0 && (
                  <div className="form-field mt-3">
                    {/* <label>Are you one of these authorized signatories?</label> */}
                    <PillSelector
                      options={authorizedSignatories.map(signatory => ({
                        value: signatory.name || signatory,
                        label: signatory.name || signatory
                      }))}
                      value={selectedSignatory || (authorizedSignatories[0]?.name || authorizedSignatories[0])}
                      onChange={val => {
                        setSelectedSignatory(val);
                        updateSection('address', { selectedSignatory: val });
                      }}
                      multiSelect={false}
                    />
                    <ErrorMessage name="selectedSignatory" component="div" className="error-message" />
                  </div>
                )}
              </div>

              {/* Address Section */}
              {(values.isVerifiedPerson === 'yes' || values.consentGiven === 'yes') && (
                <div className="form-section">

                  {/* Show fetched registered address (from PAN/GSTIN) if available */}
                  {(effectiveFetchedRegisteredAddress || effectiveFetchedAddressSource) && (
                    <div style={{
                      background: '#eef6ff',
                      border: '1px solid #b6daff',
                      borderRadius: '4px',
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      color: '#0c5460'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        This is the registered address we fetched from your {effectiveFetchedAddressSource}
                      </div>
                      {/* <div style={{ whiteSpace: 'pre-wrap' }}>{effectiveFetchedRegisteredAddress}</div> */}
                    </div>
                  )}

                  {values.isAddressFromDocMissing && (
                    <div className="manual-address-section" style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '1rem' }}>
                      <h5 style={{ marginBottom: '1rem', fontSize: '16px' }}>Please verify and complete address details:</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                        {missingAddressFields.map((key) => {
                          const labelMap = {
                            line_1: 'Address Line 1',
                            line_2: 'Address Line 2',
                            street_name: 'Address Line 3',
                            zip: 'Pin Code',
                            district_code: 'District',
                          };
                          const labelText = labelMap[key] || key.replace('_', ' ');
                          const currentLength = values.manualAddress?.[key]?.length || 0;
                          const isLineField = ['line_1', 'line_2', 'street_name'].includes(key);
                          const isOptional = ['line_2', 'street_name'].includes(key);
                          const prefetchedValue = prefetchedManualAddress?.[key] || '';

                          let isReadOnly = false;
                          if (prefetchedValue) {
                            if (isLineField) {
                              if (prefetchedValue.length >= 3 && prefetchedValue.length <= 100) {
                                isReadOnly = true;
                              }
                            } else if (key === 'zip') {
                              if (/^\d{6}$/.test(prefetchedValue)) {
                                isReadOnly = true;
                              }
                            } else {
                              if (prefetchedValue.trim() !== '') {
                                isReadOnly = true;
                              }
                            }
                          }

                          const colSpan = isLineField && !isMobile ? '1 / -1' : 'auto';

                          return (
                            <div key={key} className="form-field" style={{ gridColumn: colSpan }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <label style={{ textTransform: 'capitalize', fontSize: '12px', marginBottom: 0 }}>
                                  {labelText}
                                  {!isOptional && <span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span>}
                                </label>
                                {isLineField && (
                                  <span style={{ fontSize: '10px', color: currentLength < 3 && currentLength > 0 ? '#dc3545' : currentLength === 100 ? '#e10000' : '#6c757d', fontWeight: currentLength === 100 ? 'bold' : 'normal' }}>
                                    {currentLength}/100
                                  </span>
                                )}
                              </div>
                              <Field
                                type="text"
                                name={`manualAddress.${key}`}
                                className={`form-input ${(touched.manualAddress?.[key] && errors.manualAddress?.[key]) ? 'error' : ''}`}
                                placeholder={`Enter ${labelText}`}
                                maxLength={isLineField ? 100 : key === 'zip' ? 6 : undefined}
                                readOnly={isReadOnly}
                                style={{
                                  backgroundColor: isReadOnly ? '#f8f9fa' : 'white',
                                  cursor: isReadOnly ? 'not-allowed' : 'text',
                                  color: isReadOnly ? '#6c757d' : 'inherit'
                                }}
                                onChange={(e) => {
                                  setFieldValue(`manualAddress.${key}`, e.target.value);
                                  // if (key === 'zip') setValidatedPincode(e.target.value);
                                }}
                              />
                              {isLineField && currentLength > 0 && currentLength < 3 && !isReadOnly && (
                                <div style={{ color: '#dc3545', fontSize: '10px', marginTop: '2px' }}>This field has characters less than 3</div>
                              )}
                              {isLineField && currentLength === 100 && !isReadOnly && (
                                <div style={{ color: '#dc3545', fontSize: '10px', marginTop: '2px' }}>Maximum character limit of 100 reached</div>
                              )}
                              <ErrorMessage name={`manualAddress.${key}`} component="div" className="error-text" style={{ color: '#dc3545', fontSize: '11px', marginTop: '4px' }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Move factory address switch up */}
                  {/* {!hideFactoryToggle && (
                  <div className="form-field mb-4">
                    <label style={{ fontWeight: 500, marginRight: '1rem' }}>Is the registered address also the factory address?</label>
                    <label className="ncswitch">
                      <input
                        type="checkbox"
                        checked={values.sameAsFactory}
                        onChange={e => {
                          const checked = e.target.checked;
                          setFieldValue('sameAsFactory', checked);
                          updateSection('address', { sameAsFactory: checked });
                        }}
                      />
                      <span className="ncswitch-slider"></span>
                    </label>
                    <span style={{ fontWeight: 500, color: '#757d84', marginLeft: '0.5rem' }}>{values.sameAsFactory ? 'Yes' : 'No'}</span>
                  </div>
                  )} */}

                  {/* Only show Pick Location when factory address is different - temporarily hidden */}
                  {false && !values.sameAsFactory && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                    <button
                      type="button"
                      style={{
                        background: '#1b62ab',
                        color: 'white',
                        border: 'none',
                        borderRadius: '999px',
                        fontWeight: '500',
                        padding: '7px 24px',
                        fontSize: '1rem',
                        marginRight: '24px',
                        boxSizing: 'border-box',
                        lineHeight: 1.2,
                        cursor: !mapplsAccessToken ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        height: '40px',
                        minWidth: '170px'
                      }}
                      onMouseEnter={e => {
                        e.target.style.background = '#155a99';
                      }}
                      onMouseLeave={e => {
                        e.target.style.background = '#1b62ab';
                      }}
                      onClick={toggleLocationModal}
                      disabled={!mapplsAccessToken}
                    >
                      <span style={{
                        fontSize: '1.15em',
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 1
                      }}>📍</span>
                      Pick Location from Map
                    </button>
                    <button
                      type="button"
                      style={{
                        border: '1.5px solid #dc3545',
                        color: '#dc3545',
                        background: 'transparent',
                        fontWeight: '500',
                        padding: '7px 24px',
                        fontSize: '1rem',
                        borderRadius: '999px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        boxSizing: 'border-box',
                        lineHeight: 1.2,
                        height: '40px',
                        minWidth: '110px'
                      }}
                      onMouseEnter={e => {
                        e.target.style.backgroundColor = '#dc3545';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={e => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#dc3545';
                      }}
                      onClick={() => clearLocation(setFieldValue)}
                      title="Reset Location"
                    >
                      Reset
                    </button>
                    {!mapplsAccessToken && (
                      <small className="form-text text-muted" style={{ marginLeft: '1rem' }}>Loading map services...</small>
                    )}
                  </div>
                )}

                  {false && !values.sameAsFactory && !hasPickedLocation && (
                    <div style={{ color: '#6c757d', marginBottom: '1rem' }}>
                      Pick Location from Map to autofill your address.
                    </div>
                  )}

                  {false && (!values.sameAsFactory && hasPickedLocation) && (
                    <>
                      {/* Factory Address */}
                      <h3 className="mb-3">Factory Address</h3>
                      <div className="form-field mb-3">
                        <label htmlFor="addressLine1">Address Line 1{!values.sameAsFactory && (<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span>)}</label>
                        <Field
                          type="text"
                          name="location.addressLine1"
                          id="addressLine1"
                          className={`form-input ${touched.location?.addressLine1 && errors.location?.addressLine1 ? 'error' : ''}`}
                          placeholder="Enter address line 1"
                        />
                        <ErrorMessage name="location.addressLine1" component="div" className="error-message" />
                      </div>

                      <div className="form-field mb-3">
                        <label htmlFor="addressLine2">Address Line 2</label>
                        <Field
                          type="text"
                          name="location.addressLine2"
                          id="addressLine2"
                          className={`form-input ${touched.location?.addressLine2 && errors.location?.addressLine2 ? 'error' : ''}`}
                          placeholder="Enter address line 2"
                        />
                        <ErrorMessage name="location.addressLine2" component="div" className="error-message" />
                      </div>

                      <div className="form-field mb-3">
                        <label htmlFor="addressLine3">Address Line 3</label>
                        <Field
                          type="text"
                          name="location.addressLine3"
                          id="addressLine3"
                          className={`form-input ${touched.location?.addressLine3 && errors.location?.addressLine3 ? 'error' : ''}`}
                          placeholder="Enter address line 3"
                        />
                        <ErrorMessage name="location.addressLine3" component="div" className="error-message" />
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-field mb-3">
                            <label htmlFor="city">City{!values.sameAsFactory && (<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span>)}</label>
                            <Field
                              type="text"
                              name="location.city"
                              id="city"
                              className={`form-input disabled-field ${touched.location?.city && errors.location?.city ? 'error' : ''}`}
                              placeholder="City Autofill"
                              disabled
                            />
                            <ErrorMessage name="location.city" component="div" className="error-message" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-field mb-3">
                            <label htmlFor="state">State{!values.sameAsFactory && (<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span>)}</label>
                            <Field
                              type="text"
                              name="location.state"
                              id="state"
                              className={`form-input disabled-field ${touched.location?.state && errors.location?.state ? 'error' : ''}`}
                              placeholder="State Autofill"
                              disabled
                            />
                            <ErrorMessage name="location.state" component="div" className="error-message" />
                          </div>
                        </div>
                      </div>

                      <div className="form-field mb-3">
                        <label htmlFor="pincode">Pincode{!values.sameAsFactory && (<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span>)}</label>
                        <Field
                          type="text"
                          name="location.pincode"
                          id="pincode"
                          className={`form-input disabled-field ${touched.location?.pincode && errors.location?.pincode ? 'error' : ''}`}
                          placeholder="Pincode Autofill"
                          disabled
                        />
                        <ErrorMessage name="location.pincode" component="div" className="error-message" />
                      </div>

                      {/* Pincode mismatch warning - appears immediately after location selection */}
                      {locationPincodeMismatch && (
                        <div style={{
                          color: '#dc3545',
                          background: '#fff3cd',
                          border: '1px solid #ffeeba',
                          borderRadius: '4px',
                          padding: '0.75rem',
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <svg width="60" height="60" viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="10" fill="#ffc107"/>
                            <path d="M10 6V11M10 14H10.01" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>The pincode from your GST or PAN ({effectiveValidatedPincode}) does not match the pincode of the picked location. Please ensure your business address matches your official records.</span>
                        </div>
                      )}

                      {/* After the pincode field and pincode mismatch warning, show the district serviceability message if present */}
                      {districtServiceMessage && (
                        <div style={{
                          color: isDistrictServiceable ? '#856404' : '#dc3545',
                          background: isDistrictServiceable ? '#fff3cd' : '#f8d7da',
                          border: isDistrictServiceable ? '1px solid #ffeeba' : '1px solid #f5c6cb',
                          borderRadius: '4px',
                          padding: '0.75rem',
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <svg width="60" height="60" viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="10" fill={isDistrictServiceable ? '#ffc107' : '#dc3545'} />
                            <path d="M10 6V11M10 14H10.01" stroke={isDistrictServiceable ? '#856404' : 'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>{districtServiceMessage}</span>
                        </div>
                      )}

                      {/* Form validation error for pincode mismatch */}
                      <ErrorMessage name="pincodeMismatch" component="div" className="error-message" />
                      {/* Form validation error for district serviceability */}
                      <ErrorMessage name="districtServiceability" component="div" className="error-message" />
                      {/* Form validation error for selectedSignatory */}
                      <ErrorMessage name="selectedSignatory" component="div" className="error-message" />
                    </>
                  )}

                  {/* Info message when registered address is not factory address */}
                  {!values.sameAsFactory && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '1rem',
                      padding: '0.75rem',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      color: '#6c757d'
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="10" fill="#17a2b8"/>
                          <path d="M10 6V10M10 14H10.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>Please add Factory Address in Organization details once Onboarding is completed.</span>
                    </div>
                  )}

                  {/* <div className="form-field mb-3">
                    <label htmlFor="fullAddress">Full Address</label>
                    <Field
                      type="text"
                      name="location.fullAddress"
                      id="fullAddress"
                      className={`form-input`}
                      placeholder="Full formatted address"
                      readOnly
                    />
                  </div> */}

                  {/* <div className="form-field mb-3">
                    <label htmlFor="lat">Latitude</label>
                    <Field
                      type="text"
                      name="location.lat"
                      id="lat"
                      className={`form-input`}
                      placeholder="Latitude"
                      readOnly
                    />
                  </div> */}
                  {/* <div className="form-field mb-3">
                    <label htmlFor="lon">Longitude</label>
                    <Field
                      type="text"
                      name="location.lon"
                      id="lon"
                      className={`form-input`}
                      placeholder="Longitude"
                      readOnly
                    />
                  </div> */}
                </div>
              )}

              {/* Stop form if no consent */}
              {values.isVerifiedPerson === 'no' && values.consentGiven === 'no' && (
                <div className="mt-4" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#dc3545' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#dc3545"/>
                    <path d="M6 6L14 14M14 6L6 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                  <span>Sorry, you cannot proceed without being the registered person or having proper consent. The application will now close.</span>
                </div>
              )}

              {/* Location Modal */}
              <Modal
                isOpen={isLocationModalOpen}
                toggle={toggleLocationModal}
                size="xl"
                className="operation-location-modal"
                style={{ maxWidth: '90vw', width: '90vw' }}
              >
                <ModalHeader toggle={toggleLocationModal} className="operation-modal-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>📍</span>
                    <span style={{ fontSize: '18px', color: '#fff', fontWeight: '600' }}>Select Your Business Location</span>
                  </div>
                </ModalHeader>
                <ModalBody className="operation-modal-body">
                  <div className="operation-location-instructions">
                    <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                      Search for your business address or click on the map to select a location.
                      All address fields will be automatically filled once you select a location.
                    </p>
                  </div>
                  <div id="operationLocationMap" className="operation-location-map-container"></div>
                </ModalBody>
                <ModalFooter className="operation-modal-footer">
                  <Button color="secondary" onClick={toggleLocationModal} size="lg">
                    Cancel
                  </Button>
                  <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                    Powered by MapMyIndia
                  </div>
                </ModalFooter>
              </Modal>

              {/* Map Scripts */}
              {isLocationModalOpen && mapplsAccessToken && (
                <LocationMapModal
                  isOpen={isLocationModalOpen}
                  toggle={toggleLocationModal}
                  mapplsAccessToken={mapplsAccessToken}
                  onLocationSelect={(locationData) => handleLocationSelect(locationData, setFieldValue)}
                />
              )}

              {/* At the bottom of the form, before the submit/next button, add the soft warning if companyName is present */}
              {/* {(companyName || values.isVerifiedPerson === 'no') && (
                <div style={{
                  background: '#fff3cd',
                  border: '1px solid #ffeeba',
                  borderRadius: '4px',
                  padding: '1rem',
                  margin: '1.5rem 0 1rem 0',
                  color: '#856404',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  <svg width="32" height="32" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="10" cy="10" r="10" fill="#ffc107"/>
                    <path d="M10 6V11M10 14H10.01" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>
                    <b>Please note:</b> Impersonating someone else online or unauthorised action is punishable under Sections 66C and 66D of the Information Technology Act, 2000 (IT Act) in India.<br /><br />
                    By clicking <b>Next</b>, you confirm that all information provided here is valid and accurate, and that you are authorised to act on behalf of <b>{companyName}</b>.
                  </span>
                </div>
              )} */}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
});

// Location Map Modal Component
const LocationMapModal = ({ isOpen, toggle, mapplsAccessToken, onLocationSelect }) => {
  useEffect(() => {
    if (!isOpen || !mapplsAccessToken) return;

    let map = null;

    const loadMapScripts = async () => {
      try {
        // Load MapMyIndia scripts
        // await loadScript(`https://apis.mapmyindia.com/advancedmaps/v1/${mapToken}/map_load?v=1.5`);
        // await loadScript(`https://apis.mapmyindia.com/advancedmaps/api/${mapToken}/map_sdk_plugins`);

        await loadScript(`https://sdk.mappls.com/map/sdk/web?v=3.0&access_token=${mapplsAccessToken}`);
        await loadScript(`https://sdk.mappls.com/map/sdk/plugins?access_token=${mapplsAccessToken}&v=3.0`);

        if (!window.mappls) {
          console.error('Mappls SDK not loaded');
          return;
        }

        // Initialize map with larger size
        // const map = new MapmyIndia.Map(document.getElementById('operationLocationMap'), {
        //   center: [20.5937, 78.9629], // Center of India
        //   zoom: 5,
        //   search: false,
        // });

        map = new window.mappls.Map(document.getElementById('operationLocationMap'), {
          center: [20.5937, 78.9629],
          zoom: 5,
        });

        console.log('mappls placepicker >>', window.mappls);

        map.addListener('load', () => {
          const options = {
            // map: map,
            // callback: (data) => {
            //   onLocationSelect(data);
            // },
            // geolocation: true,
            // closeBtn: false,
            // search: true,
            // topText: 'Search for your business location',
            // pinImage: 'https://apis.mapmyindia.com/map_v3/1.png',
            // pinHeight: 40,

            map,
            header: true,
            closeBtn: false,
            geolocation: true,
            pinImage: 'https://apis.mappls.com/map_v3/1.png',
            topText: 'Search for your business location',
            pinHeight: 40,
          };

          window.mappls.placePicker(options, callback_method);

          async function callback_method(picker) {
            if (picker?.data && picker?.data !== undefined) {
              onLocationSelect(picker?.data);
            }
          }
        });
      } catch (error) {
        console.error('Error loading map:', error);
      }
    };

    loadMapScripts();

    // return () => {
    //   if (map?.remove) {
    //     map.remove();
    //   }
    // };
  }, [isOpen, mapplsAccessToken, onLocationSelect]);

  return null;
};

// Helper function to load scripts
// const loadScript = (url) => {
//   return new Promise((resolve, reject) => {
//     const script = document.createElement('script');
//     script.src = url;
//     script.async = false;
//     script.onload = () => resolve(url);
//     script.onerror = () => reject(url);
//     document.body.appendChild(script);
//     setTimeout(() => {
//       if (document.body.contains(script)) {
//         document.body.removeChild(script);
//       }
//     }, 100);
//   });
// };

function loadScript(url) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${url}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    script.onload = resolve;
    script.onerror = reject;

    document.body.appendChild(script);
  });
}

export default OperationDetails;
